import copyIcon from '@disy/cadenza-icons/copy-to-clipboard.svg';
import { assert } from '@disy/lib/custom-error';
import { icon } from '@disy/lib/icon';
import { createButton, createIconButton } from '@disy/ui/button';
import type { HyperScriptButtonOptions } from '@disy/ui/button';
import { showToastMessage } from '@disy/ui/toast-message';

import i18n from './clipboard-button.properties';

/**
 * Creates a button to copy the given text to the clipboard.
 *
 * @param text - The text to copy or a supplier therefore
 * @param [options]
 * @param [options.toast] - A message to display when copying was successful
 * @param [options.styleClass] - A style class that is applied to the button
 */
export function createClipboardButton(
  text: string | (() => string),
  {
    toast,
    styleClass,
    variant,
    iconOnly = true,
    size = 's',
  }: { toast?: string; iconOnly?: boolean } & Pick<HyperScriptButtonOptions, 'styleClass' | 'variant' | 'size'> = {},
) {
  const options: HyperScriptButtonOptions = {
    variant,
    size,
    onclick: () => copyToClipboard(text, { toast }),
    styleClass,
  };
  return iconOnly ?
      createIconButton(icon(copyIcon), i18n('title'), options)
    : createButton([icon(copyIcon), i18n('title')], options);
}

/**
 * Copies the given text to the clipboard.
 *
 * @param text - The text to copy
 * @param options
 * @param options.showToast - Whether to show a message when copying was successful
 * @param options.toast - A message to display when copying was successful
 */
export function copyToClipboard(text: string | (() => string), { showToast = true, toast = i18n('toast') } = {}) {
  copyToClipboardInternal(typeof text === 'function' ? text() : text)
    .then(() => {
      if (showToast) {
        showToastMessage(toast, { type: 'success' });
      }
    })
    // There is intentionally no option for the error message, because an error is largely an edge
    // case, and we should not bother team technical writing to come up with an error message for
    // each usage of this util.
    .catch(() => showToastMessage(i18n('toast.error'), { type: 'error' }));
}

function copyValueToClipboard(input: HTMLInputElement | HTMLTextAreaElement) {
  assert(document.body.contains(input), 'The input element must be connected to the DOM');

  const activeElement = document.activeElement;
  const disabled = input.disabled;

  input.disabled = false;
  input.select();

  document.execCommand('copy');

  input.disabled = disabled;

  if (activeElement instanceof HTMLElement) {
    activeElement.focus();
  }
}

/**
 * Asynchronously (when the API is available) write the given text value to the clipboard.
 *
 * @param text - Text to write
 * @return A promise that is fulfilled when the text has been copied
 */
function copyToClipboardInternal(text: string) {
  if (navigator.clipboard) {
    return navigator.clipboard.writeText(text);
  } else {
    // The navigator.clipboard API is disabled when we're not in a secure context (e.g. https:// or localhost).
    // Workaround: create dummy input
    const input = document.createElement('input');
    input.value = text;
    document.body.append(input);
    copyValueToClipboard(input);
    input.remove();
    return Promise.resolve();
  }
}
